import { formatSupabaseError } from './formatSupabaseError';
import { getDbSupabase } from './supabase';
import type { DemWhatsAppQueueMessage } from '../types/dem';

const SGT = 'Asia/Singapore';

function formatQueueTime(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : `${raw.replace(' ', 'T')}+00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return raw.slice(0, 16).replace('T', ' ');
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: SGT,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return raw.slice(0, 16).replace('T', ' ');
  }
}

function mapQueueRow(row: Record<string, unknown>): DemWhatsAppQueueMessage {
  return {
    id: String(row.id ?? ''),
    customer_id: String(row.customer_id ?? 'Unknown').trim(),
    message: String(row.message ?? ''),
    status: String(row.status ?? 'Pending'),
    created_at: String(row.created_at ?? ''),
    created_at_display: formatQueueTime(String(row.created_at ?? '')),
  };
}

export async function fetchPendingWhatsAppQueue(): Promise<DemWhatsAppQueueMessage[]> {
  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('whatsapp_queue')
    .select('id, customer_id, message, status, created_at')
    .eq('status', 'Pending')
    .order('created_at', { ascending: true });

  if (error) throw new Error(formatSupabaseError(error));
  return (data ?? []).map((row) => mapQueueRow(row as Record<string, unknown>));
}

export async function dismissWhatsAppQueueMessages(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const db = getDbSupabase('dem');
  const { error } = await db.from('whatsapp_queue').update({ status: 'Dismissed' }).in('id', ids);
  if (error) throw new Error(formatSupabaseError(error));
}

export async function markWhatsAppQueueProcessed(ids: string[]): Promise<void> {
  if (ids.length === 0) return;
  const db = getDbSupabase('dem');
  const { error } = await db.from('whatsapp_queue').update({ status: 'Processed' }).in('id', ids);
  if (error) throw new Error(formatSupabaseError(error));
}

export type WaQueueProcessPayload = {
  ids: string[];
  customerId: string;
  message: string;
};

export function mergeWaQueueMessages(messages: DemWhatsAppQueueMessage[]): WaQueueProcessPayload | null {
  if (messages.length === 0) return null;
  const customerIds = new Set(messages.map((m) => m.customer_id));
  if (customerIds.size > 1) {
    throw new Error('Please select messages from the same customer / WhatsApp group.');
  }
  const sorted = [...messages].sort((a, b) => a.created_at.localeCompare(b.created_at));
  return {
    ids: sorted.map((m) => m.id),
    customerId: sorted[0]!.customer_id,
    message: sorted.map((m) => m.message.trim()).filter(Boolean).join('\n\n'),
  };
}
